import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import { ThreadWithComments, CommentType } from '@distribute/shared/types';
import { IconMap } from '../../../shared/sprite';
import { Icon } from '../../../shared/ui';
import { CommentCard } from './CommentCard';
import { formatRelativeTime } from '../utils/formatRelativeTime';
import { authUserModel } from '../../../entities/auth-user';
import { useTiptapEditor } from '../../../entities/tiptap-editor';
import { CommentsEditorForm } from './CommentsEditorForm';
import { commentsModel } from '../model';
import { JSONContent } from '@tiptap/react';

type ThreadItemProps = {
  thread: ThreadWithComments;
  isCompact?: boolean;
};

const ThreadSelectionType = {
  [CommentType.Inline]: {
    text: 'Selected text',
    icon: IconMap.TextA,
  },
};

export const ThreadItem: React.FC<ThreadItemProps> = ({
  thread,
  isCompact = false,
}) => {
  const dispatch = useDispatch();
  const { resolved, resolvedAt, resolvedById, resolvedBy, markedAsDeleted } =
    thread;
  const [isRepliesVisible, setIsRepliesVisible] = useState(false);
  const [isCreateReplyFormVisible, setIsCreateReplyFormVisible] =
    useState(false);
  const currentUser = useSelector(authUserModel.selectors.selectUserWithError);
  const isCommentReplying = useSelector(
    commentsModel.selectors.selectIsCommentReplying
  );
  const isResolvedByOwner = resolvedById === currentUser.id;
  const resolvedByUserName = `${resolvedBy?.displayName} ${
    isResolvedByOwner ? '(You)' : ''
  }`;
  const selectedType = ThreadSelectionType[thread.type as CommentType.Inline];
  const [firstComment, ...restComments] = thread.comments;
  const hasReplies = restComments.length > 0;
  const isResolved = resolved && resolvedAt && resolvedById;
  const isAddReplyBtnVisible = !isResolved && !isCreateReplyFormVisible;
  const { editor } = useTiptapEditor();

  const handleSelectCommentThread = () => {
    if (markedAsDeleted) return;

    editor?.commands.selectThread({
      id: thread.id,
      focus: true,
      scrollIntoView: true,
    });
  };

  const handleSubmitReply = (content: JSONContent) => {
    dispatch(
      commentsModel.actions.createCommentReply({
        threadId: thread.id,
        content,
        cb: (err: unknown) => {
          if (err) return;
          setIsCreateReplyFormVisible(false);
        },
      })
    );
  };

  return (
    <div
      className={classNames('bg-base-white', {
        'border border-gray-300 rounded-xl hover:shadow-sm overflow-hidden':
          !isCompact,
      })}
      onClick={handleSelectCommentThread}
    >
      {!isCompact && (
        <header className="flex flex-col gap-2 bg-gray-100 text-gray-700 px-4 py-2">
          {selectedType && !markedAsDeleted && (
            <div className="flex gap-2 items-center">
              <Icon glyph={selectedType.icon} width={16} height={16} />
              <p className="">{selectedType.text}</p>
            </div>
          )}
          {markedAsDeleted && (
            <div className="flex gap-2 items-center">
              <Icon glyph={IconMap.Trash} width={16} height={16} />
              <p className="">Content was removed.</p>
            </div>
          )}
          {isResolved && (
            <div className="text-gray-500">
              <p className="text-xs">Resolved by {resolvedByUserName}</p>
              <span className="text-gray-500 text-xs">
                {formatRelativeTime(resolvedAt)}
              </span>
            </div>
          )}
        </header>
      )}
      <div className="">
        <CommentCard
          isMain
          comment={firstComment}
          threadId={thread.id}
          isResolved={thread.resolved}
        />
        <div
          className={classNames('pl-4', {
            'flex gap-2': !isRepliesVisible,
          })}
        >
          {hasReplies && !isRepliesVisible && (
            <p
              className="text-sm text-primary-700 font-semibold cursor-pointer pb-4"
              onClick={() => setIsRepliesVisible(true)}
            >
              {restComments.length}{' '}
              {restComments.length > 1 ? 'replies' : 'reply'}
            </p>
          )}
          {hasReplies && isRepliesVisible && (
            <div className="border-t border-gray-200 pl-4">
              {restComments.map((comment) => (
                <CommentCard
                  key={comment.id}
                  comment={comment}
                  threadId={thread.id}
                  isResolved={thread.resolved}
                />
              ))}
            </div>
          )}
          {isAddReplyBtnVisible && (
            <p
              className="text-sm text-primary-700 font-semibold cursor-pointer pb-4"
              onClick={() => setIsCreateReplyFormVisible(true)}
            >
              Add reply
            </p>
          )}
        </div>
      </div>
      {isCreateReplyFormVisible && (
        <div className="bg-gray-100 p-3">
          <CommentsEditorForm
            isEditable
            disabled={isCommentReplying}
            onCancel={() => setIsCreateReplyFormVisible(false)}
            onSubmit={handleSubmitReply}
          />
        </div>
      )}
    </div>
  );
};
